/**
 * @file viewportFit.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description How wide the page lays itself out on a phone. Cam, 28 Oct
 *              2026: "Can you make sure the 'zoom' or whatever it is, when
 *              the app is opened on iPhone, is corrected/ defaulted/
 *              normalised to fit everything in without horizontal overflow".
 *
 * Two kinds of person open the system on a phone:
 *  - a rep, who gets the phone shell (data-rep-mobile) — laid out for the
 *    phone's own width: `width=device-width`, as index.html says;
 *  - anyone else (Cam, the office), who keeps the desktop layout on purpose.
 *    At the phone's own 390px width that layout ran off the side, so the
 *    page opened zoomed in on its left edge with a sideways scroll. For them
 *    the viewport is set to the desktop's design width instead, and the
 *    phone shows the whole desktop scaled to fit — pinch-zoom still works
 *    (nothing here sets user-scalable or a maximum scale).
 *
 * "A phone" is judged from the DEVICE's screen, not the layout viewport:
 * once the layout is 1280px wide every width media query says "desktop", so
 * deciding from the layout would undo itself the moment it was applied. Only
 * a touch screen whose short side is at most 768px counts — a laptop with a
 * narrow window is never resized.
 *
 * Pure functions plus one that writes the meta tag; Dashboard decides when.
 */

/** The width the desktop layout is designed for. */
export const DESKTOP_LAYOUT_WIDTH = 1280;

/** What index.html ships: the phone's own width, edge to edge. */
export const DEVICE_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';

/** The desktop, scaled to fit the phone. No initial-scale: the browser fits it. */
export const DESKTOP_VIEWPORT = `width=${DESKTOP_LAYOUT_WIDTH}, viewport-fit=cover`;

/**
 * Is this a phone-sized touch screen? `screen` and `coarse` are passed in by
 * the tests; in the app they come from the window.
 */
export function isSmallTouchScreen({
  screen = (typeof window !== 'undefined' ? window.screen : null),
  coarse = (typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false)
} = {}) {
  if (!screen || !coarse) return false;
  const w = Number(screen.width) || 0;
  const h = Number(screen.height) || 0;
  const short = Math.min(w, h);
  return short > 0 && short <= 768;
}

/**
 * The viewport content for who is signed in, on what. A rep (the phone shell
 * is theirs whenever the screen is narrow — never taken away by this) and
 * anyone not signed in (the sign-in page is laid out for the phone) get the
 * device width; anyone else on a phone-sized touch screen gets the desktop
 * scaled to fit.
 */
export function viewportContentFor({ signedIn = false, rep = false, repMobile = false, smallTouchScreen = false } = {}) {
  if (!signedIn || rep || repMobile || !smallTouchScreen) return DEVICE_VIEWPORT;
  return DESKTOP_VIEWPORT;
}

/** Writes the viewport meta tag, only when it differs. */
export function applyViewport(content, doc = (typeof document !== 'undefined' ? document : null)) {
  if (!doc || !content) return;
  let meta = doc.querySelector('meta[name="viewport"]');
  if (!meta) {
    meta = doc.createElement('meta');
    meta.setAttribute('name', 'viewport');
    doc.head.appendChild(meta);
  }
  if (meta.getAttribute('content') !== content) meta.setAttribute('content', content);
}
